import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { CheckCircleIcon } from '@heroicons/react/24/outline'

import { INTERAX_COLORS } from '../../../../constants/colors'
import { OnboardingStepProgress, type OnboardingStepProgressItem } from './OnboardingStepProgress'
import { OnboardingStepNav } from './OnboardingStepNav'

interface OrgOnboardingScaffoldNav {
  isFirstStep: boolean
  isLastStep: boolean
  primaryLoading: boolean
  primaryLabel: string
  backLabel: string
  onBack: () => void
  onPrimary: () => void
}

interface OrgOnboardingScaffoldProps {
  preTitle: string
  title: string
  subtitle: string
  steps: OnboardingStepProgressItem[]
  currentStep: number
  mobileStepLabel: string
  errorMessage?: string | null
  nav: OrgOnboardingScaffoldNav
  children: React.ReactNode
}

/** Full-page presentational shell for the org onboarding wizard (background, header, steps, nav). */
export const OrgOnboardingScaffold: React.FC<OrgOnboardingScaffoldProps> = ({
  preTitle,
  title,
  subtitle,
  steps,
  currentStep,
  mobileStepLabel,
  errorMessage,
  nav,
  children
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: INTERAX_COLORS.offBlack
      }}
    >
      {/* Full-bleed mountain backdrop (wordmark is baked into the image, top-left). */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(180deg, rgba(18,21,23,0.82) 0%, rgba(18,21,23,0.74) 45%, rgba(18,21,23,0.58) 100%), url(/assets/img/onboarding_background.png)`,
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundSize: 'cover, cover',
          // Anchor the image to the top-left so its baked-in wordmark is never cropped.
          backgroundPosition: 'center, left top'
        }}
      />

      {/* Centered content (top padding leaves room for the image's baked-in wordmark) */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 5, sm: 7, md: 8 },
          pb: { xs: 6, sm: 8 }
        }}
      >
        <Stack spacing={{ xs: 3, sm: 3.5, md: 4 }} sx={{ maxWidth: 720, mx: 'auto', width: '100%' }}>
          {/* Welcome header */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ justifyContent: 'center' }}>
            <Box
              sx={{
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                borderRadius: '14px',
                backgroundColor: 'rgba(255,255,255,0.04)',
                border: `1px solid ${INTERAX_COLORS.white10}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <CheckCircleIcon style={{ width: 28, height: 28, color: INTERAX_COLORS.sun }} />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: INTERAX_COLORS.sun, letterSpacing: 1.6, fontWeight: 700, display: 'block', mb: 0.25 }}
              >
                {preTitle}
              </Typography>
              <Typography
                sx={{ color: INTERAX_COLORS.white, fontWeight: 800, lineHeight: 1.15, fontSize: { xs: 26, sm: 32 } }}
              >
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey, mt: 0.5, fontWeight: 500 }}>
                {subtitle}
              </Typography>
            </Box>
          </Stack>

          <OnboardingStepProgress steps={steps} currentStep={currentStep} mobileStepLabel={mobileStepLabel} />

          <Box>{children}</Box>

          {errorMessage ? (
            <Box
              role="alert"
              sx={{
                borderRadius: '10px',
                border: `1px solid ${INTERAX_COLORS.redError}`,
                backgroundColor: INTERAX_COLORS.redErrorBackground,
                px: 2,
                py: 1.5
              }}
            >
              <Typography sx={{ color: INTERAX_COLORS.redError, fontSize: 14 }}>{errorMessage}</Typography>
            </Box>
          ) : null}

          <OnboardingStepNav
            isFirstStep={nav.isFirstStep}
            isLastStep={nav.isLastStep}
            primaryLoading={nav.primaryLoading}
            primaryLabel={nav.primaryLabel}
            backLabel={nav.backLabel}
            onBack={nav.onBack}
            onPrimary={nav.onPrimary}
          />
        </Stack>
      </Box>
    </Box>
  )
}
